import { Card, Flex, IconButton } from '@brightdrop/bd-ui';
import {
  Divider,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  useTheme,
} from '@mui/material';
import { skipToken } from '@reduxjs/toolkit/query/react';
import { type MouseEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  useGetOrgEnrollmentQuery,
  useUnlinkOrgEnrollmentMutation,
} from '~/api/generated-api';
import DotsHorizontalIcon from '~/assets/icons/dotsHorizontal.svg?react';
import TranslatedLabel, {
  translateText,
} from '~/common/components/TranslatedLabel/TranslatedLabel';
import { useOrgId } from '~/common/hooks/useOrgId';
import { RouteName } from '~/common/models/route.model';
import OverviewFieldList from '~/common/overviewDetails/OverviewFieldList';
import { makeRoutePathLite } from '~/common/utils/route/route.utils';
import PartnerLogo from '~/features/integrations/partners/PartnerLogo';
import type {
  ManageSectionProps,
  PartnerId,
} from '~/features/integrations/partners/types';

import useStyles from './AccountDetailsCard.styles';
import useOverviewCardStyles from './overview-card.styles';
import UnlinkConfirmDialog from './UnlinkConfirmDialog';

const TRANSLATION_KEYS = {
  title: 'integrations:account.manage.accountDetails',
  actions: 'integrations:account.manage.accountActions',
  managePartner: 'integrations:account.manage.managePartner',
  unlinkAccount: 'integrations:account.manage.unlinkAccount',
} as const;

const ACTIONS_MENU_ID = 'account-integrations-account-actions-menu';

/**
 * Account details for a linked partner. Field rows come from that partner's manage config.
 */
const AccountDetailsCard = ({
  partner,
  linkedPartnerIds = [],
  isLinkedPartnersReady = false,
}: ManageSectionProps): JSX.Element => {
  const { classes, cx } = useStyles();
  const { classes: cardClasses } = useOverviewCardStyles();
  const theme = useTheme();
  const [actionsAnchor, setActionsAnchor] = useState<HTMLElement | null>(null);
  const isActionsOpen = Boolean(actionsAnchor);
  // Labels resolve during render, so subscribe or they go stale on a locale change.
  useTranslation(['integrations', 'common']);
  const organizationsId = useOrgId();
  const { manage, providerCode, id, nameKey, shortNameKey } = partner;
  const partnerName = translateText(nameKey);
  const closeActions = () => setActionsAnchor(null);
  const { currentData, isError } = useGetOrgEnrollmentQuery(
    organizationsId
      ? { 'cs-organization': organizationsId, providerCode }
      : skipToken
  );
  // A rejected read leaves the previous row on the cache entry, and a
  // `success: false` body is still stored when the HTTP status was 200.
  const enrollment =
    !isError && currentData?.success ? currentData.result : undefined;

  const navigate = useNavigate();
  const [isUnlinkConfirmOpen, setIsUnlinkConfirmOpen] = useState(false);
  const [hasUnlinkError, setHasUnlinkError] = useState(false);
  const [unlinkOrgEnrollment, { isLoading: isUnlinking }] =
    useUnlinkOrgEnrollmentMutation();
  // Read after the DELETE settles, so routing uses the list as it is then, not at click time.
  const completeLinkedPartnerIdsRef = useRef<PartnerId[]>([]);
  useEffect(() => {
    if (isLinkedPartnersReady) {
      completeLinkedPartnerIdsRef.current = linkedPartnerIds;
    }
  }, [isLinkedPartnersReady, linkedPartnerIds]);

  const openUnlinkConfirm = () => {
    closeActions();
    setHasUnlinkError(false);
    setIsUnlinkConfirmOpen(true);
  };

  const handleUnlink = async () => {
    setHasUnlinkError(false);
    try {
      const response = await unlinkOrgEnrollment({
        'cs-organization': organizationsId,
        providerCode,
      }).unwrap();
      if (!response.success) throw new Error('Unlink rejected');
    } catch {
      setHasUnlinkError(true);
      return;
    }
    const hasOtherLinkedPartner = completeLinkedPartnerIdsRef.current.some(
      (linkedId) => linkedId !== id
    );
    navigate(
      hasOtherLinkedPartner
        ? makeRoutePathLite({
            routeName: RouteName.ACCOUNT_ORG_INTEGRATIONS,
            params: {},
          })
        : makeRoutePathLite({
            routeName: RouteName.ORGANIZATIONS_SHOP_INTEGRATIONS,
            params: { organizationsId },
          })
    );
  };

  return (
    <Card dataTestId="account-integrations-account-details">
      <Flex className={cx(classes.aligned, classes.header)}>
        <Flex className={classes.aligned}>
          <TranslatedLabel
            translationKey={TRANSLATION_KEYS.title}
            typographyVariant="header2_medium"
            className={cardClasses.title}
            component="h2"
          />
        </Flex>
        <Flex className={classes.aligned}>
          <PartnerLogo
            partnerId={id}
            variant="wordmark"
            className={classes.logo}
            aria-hidden
          />
          <IconButton
            icon={<DotsHorizontalIcon />}
            aria-label={translateText(TRANSLATION_KEYS.actions)}
            aria-haspopup="menu"
            aria-expanded={isActionsOpen}
            aria-controls={isActionsOpen ? ACTIONS_MENU_ID : undefined}
            data-testid="account-integrations-account-actions"
            onClick={(event: MouseEvent<HTMLButtonElement>) =>
              setActionsAnchor(event.currentTarget)
            }
          />
          <Menu
            id={ACTIONS_MENU_ID}
            anchorEl={actionsAnchor}
            open={isActionsOpen}
            onClose={closeActions}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            data-testid="account-integrations-account-actions-menu"
          >
            <ListSubheader
              data-testid="account-integrations-manage-partner"
              sx={{ width: '220px', position: 'static' }}
            >
              <TranslatedLabel
                translationKey={TRANSLATION_KEYS.managePartner}
                typographyVariant="body_regular"
                variables={{ partner: partnerName }}
              />
            </ListSubheader>
            <Divider />
            <MenuItem
              onClick={openUnlinkConfirm}
              // Where to go after unlinking depends on the full partner list.
              disabled={!isLinkedPartnersReady}
              data-testid="account-integrations-unlink-account"
              sx={{ width: '220px' }}
            >
              <ListItemText
                primary={
                  <TranslatedLabel
                    translationKey={TRANSLATION_KEYS.unlinkAccount}
                    typographyVariant="body_regular"
                    style={{ color: theme.new.color.errorPrimary }}
                  />
                }
              />
            </MenuItem>
          </Menu>
        </Flex>
      </Flex>
      <OverviewFieldList
        fields={manage.accountFields(enrollment)}
        testId="partner-account"
        paddedRows
      />
      <UnlinkConfirmDialog
        isOpen={isUnlinkConfirmOpen}
        partnerName={translateText(shortNameKey ?? nameKey)}
        bodyKey={manage.unlinkConfirmBodyKey}
        isUnlinking={isUnlinking}
        hasError={hasUnlinkError}
        onConfirm={handleUnlink}
        onClose={() => setIsUnlinkConfirmOpen(false)}
      />
    </Card>
  );
};

export default Object.assign(AccountDetailsCard, {
  sectionId: 'account-details',
});
